import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import ToggleSwitch from 'Components/pos2u/Buttons/ToggleSwitch';
import { useSettings } from 'pages/SettingsV2/SettingsContext';
import { TOGGLE_SWITCH_SIZE } from 'pages/SettingsV2/SettingsConstants';

const OrderPrintSection = () => {
  const { t } = useTranslation();
  const { access } = useSettings();
  const [enabled, setEnabled] = useState(false);
  const [savedEnabled, setSavedEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const accessible = access.kitchen_ticket_printing;

  const dirty = enabled !== savedEnabled;

  const handleCancel = () => {
    setEnabled(savedEnabled);
  };

  const handleSave = () => {
    setSaving(true);
    setSavedEnabled(enabled);
    setSaving(false);
  };

  return (
    <Card className="shadow-none border mb-0">
      <CardHeader className="bg-transparent border-bottom">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <div className="d-flex align-items-start gap-3">
            <div
              className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                dirty
                  ? 'bg-warning-subtle text-warning'
                  : 'bg-success-subtle text-success'
              }`}
              style={{ width: 38, height: 38 }}
            >
              <i
                className={`fs-5 ${
                  dirty ? 'ri-error-warning-line' : 'ri-printer-line'
                }`}
              />
            </div>

            <div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <h5 className="card-title mb-0 fw-semibold">
                  {t('Order Print Settings')}
                </h5>
              </div>
              <p
                className={`mb-0 mt-1 fs-13 ${
                  dirty ? 'text-warning' : 'text-muted'
                }`}
              >
                {dirty ? t('You have unsaved changes') : t('All changes saved')}
              </p>
            </div>
          </div>

          {accessible ? (
            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
              <ToggleSwitch
                id="order-print-enabled"
                checked={enabled}
                label={t('Enabled')}
                size={TOGGLE_SWITCH_SIZE}
                disabled={saving}
                onChange={(e) => setEnabled(e.target.checked)}
              />

              <div className="d-flex gap-2">
                <Button
                  color="light"
                  className="w-100 w-sm-auto"
                  disabled={!dirty || saving}
                  onClick={handleCancel}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  color="primary"
                  className="w-100 w-sm-auto d-inline-flex align-items-center justify-content-center"
                  disabled={!dirty || saving}
                  onClick={handleSave}
                  aria-label={saving ? t('Saving...') : t('Save')}
                >
                  {saving ? <Spinner size="sm" /> : t('Save')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </CardHeader>
      {!accessible ? (
        <div className="px-3 px-lg-4 pt-3">
          <div className="border rounded-3 bg-light-subtle px-3 py-3 d-flex align-items-start gap-3">
            <div className="text-warning fs-4 lh-1">
              <i className="ri-lock-2-line" />
            </div>
            <div>
              <div className="fw-semibold text-body">
                {t("This section isn't available right now")}
              </div>
              <div className="text-muted fs-13 mt-1">
                {t(
                  "Your account or current plan doesn't support access to this section. Please contact your administrator or POS2U Support for help."
                )}
              </div>
              <div className="text-primary fs-13 fw-semibold mt-2">
                {t('Contact Support')}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <CardBody
        className={accessible ? undefined : 'opacity-50 pe-none'}
        style={{ minHeight: 48 }}
      />
    </Card>
  );
};

export default OrderPrintSection;
